import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import { TableSkeleton } from "../components/skeletons";
import { EmptyState } from "../components/EmptyState";
import {
  Plus, Search, AlertTriangle, Bug, Clock, CheckCircle2, XCircle,
  ChevronRight, AlertCircle, Zap, Shield, Eye, ArrowRight
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const Incidents = () => {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const [incidents, setIncidents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [viewingIncident, setViewingIncident] = useState(null);
  const [timeline, setTimeline] = useState([]);

  const [newIncident, setNewIncident] = useState({
    title: "",
    description: "",
    category: "unauthorized_access",
    severity: "P3",
    detection_source: "user_report",
    affected_assets: []
  });

  const categories = [
    { value: "data_breach", labelKey: "inc.cat.data_breach" },
    { value: "ransomware", labelKey: "inc.cat.ransomware" },
    { value: "phishing", labelKey: "inc.cat.phishing" },
    { value: "insider_threat", labelKey: "inc.cat.insider_threat" },
    { value: "ddos", labelKey: "inc.cat.ddos" },
    { value: "unauthorized_access", labelKey: "inc.cat.unauthorized_access" },
    { value: "system_failure", labelKey: "inc.cat.system_failure" },
    { value: "service_disruption", labelKey: "inc.cat.service_disruption" },
    { value: "physical_security", labelKey: "inc.cat.physical_security" },
    { value: "supply_chain", labelKey: "inc.cat.supply_chain" },
    { value: "other", labelKey: "inc.cat.other" }
  ];

  const severities = [
    { value: "P1", labelKey: "inc.sev.p1", color: "bg-red-500", badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    { value: "P2", labelKey: "inc.sev.p2", color: "bg-orange-500", badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
    { value: "P3", labelKey: "inc.sev.p3", color: "bg-yellow-500", badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    { value: "P4", labelKey: "inc.sev.p4", color: "bg-green-500", badge: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" }
  ];

  const statuses = [
    { value: "new", labelKey: "inc.status.new", icon: AlertCircle, color: "text-yellow-500" },
    { value: "triaged", labelKey: "inc.status.triaged", icon: Eye, color: "text-blue-500" },
    { value: "investigating", labelKey: "inc.status.investigating", icon: Search, color: "text-purple-500" },
    { value: "contained", labelKey: "inc.status.contained", icon: Shield, color: "text-orange-500" },
    { value: "eradicated", labelKey: "inc.status.eradicated", icon: Zap, color: "text-teal-500" },
    { value: "recovering", labelKey: "inc.status.recovering", icon: Clock, color: "text-indigo-500" },
    { value: "closed", labelKey: "inc.status.closed", icon: CheckCircle2, color: "text-green-500" }
  ];

  const detectionSources = [
    { value: "user_report", labelKey: "inc.src.user_report" },
    { value: "siem_alert", labelKey: "inc.src.siem_alert" },
    { value: "threat_intel", labelKey: "inc.src.threat_intel" },
    { value: "vulnerability_scan", labelKey: "inc.src.vulnerability_scan" },
    { value: "third_party", labelKey: "inc.src.third_party" },
    { value: "internal_audit", labelKey: "inc.src.internal_audit" },
    { value: "other", labelKey: "inc.src.other" }
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [incidentsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIncidents(incidentsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async (incidentId) => {
    try {
      const response = await axios.get(`${API}/incidents/${incidentId}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(response.data);
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
    }
  };

  const handleAddIncident = async () => {
    if (!newIncident.title || !newIncident.description) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/incidents`, newIncident, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Incident created successfully");
      setShowAddIncident(false);
      setNewIncident({
        title: "",
        description: "",
        category: "unauthorized_access",
        severity: "P3",
        detection_source: "user_report",
        affected_assets: []
      });
      fetchData();
    } catch (error) {
      toast.error(extractError(error, "Failed to create incident"));
    }
  };

  const handleUpdateStatus = async (incidentId, newStatus) => {
    try {
      await axios.put(`${API}/incidents/${incidentId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (viewingIncident?.incident_id === incidentId) {
        setViewingIncident({ ...viewingIncident, status: newStatus });
        fetchTimeline(incidentId);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openIncidentDetail = async (incident) => {
    setViewingIncident(incident);
    await fetchTimeline(incident.incident_id);
  };

  const getSeverityBadge = (severity) => {
    return severities.find(s => s.value === severity)?.badge || "";
  };

  const getStatusConfig = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const filteredIncidents = incidents.filter(inc => {
    const matchesSearch = inc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = selectedSeverity === "all" || inc.severity === selectedSeverity;
    const matchesStatus = selectedStatus === "all" || inc.status === selectedStatus;
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const incidentStats = {
    total: incidents.length,
    open: incidents.filter(i => !["closed"].includes(i.status)).length,
    critical: incidents.filter(i => i.severity === "P1" && i.status !== "closed").length,
    bySeverity: severities.map(s => ({
      ...s,
      count: incidents.filter(i => i.severity === s.value).length
    }))
  };

  const getStatusProgress = (status) => {
    const index = statuses.findIndex(s => s.value === status);
    return ((index + 1) / statuses.length) * 100;
  };

  if (loading) {
    return <TableSkeleton rows={6} columns={6} label="Loading incidents" />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="incidents-page">
      <PageHeader
        title={t("inc.title")}
        description={t("inc.subtitle")}
        actions={
          <Button onClick={() => setShowAddIncident(true)} data-testid="add-incident-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("inc.new")}
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Bug className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{incidentStats.total}</p>
                <p className="text-xs text-muted-foreground">{t("incidents.total")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{incidentStats.open}</p>
                <p className="text-xs text-muted-foreground">{t("incidents.open")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Zap className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{incidentStats.critical}</p>
                <p className="text-xs text-muted-foreground">{t("incidents.critical_p1")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {incidents.filter(i => i.status === "closed").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("incidents.resolved")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("incidents.search_ph")}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="incident-search-input"
          />
        </div>
        <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
          <SelectTrigger className="w-full md:w-36" data-testid="severity-filter">
            <SelectValue placeholder={t("incidents.severity")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("incidents.all_severities")}</SelectItem>
            {severities.map(s => (
              <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full md:w-36" data-testid="status-filter">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("incidents.all_statuses")}</SelectItem>
            {statuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Incidents List */}
      <ScrollArea className="h-[calc(100vh-28rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {filteredIncidents.map((incident, index) => {
              const statusConfig = getStatusConfig(incident.status);
              const StatusIcon = statusConfig.icon;

              return (
                <motion.div
                  key={incident.incident_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                >
                  <Card
                    className="border-border hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => openIncidentDetail(incident)}
                    data-testid={`incident-card-${incident.incident_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline" className={getSeverityBadge(incident.severity)}>
                              {incident.severity}
                            </Badge>
                            <Badge variant="secondary" className="capitalize">
                              {t(categories.find(c => c.value === incident.category)?.labelKey || "inc.cat.other")}
                            </Badge>
                            <span className={`flex items-center gap-1 text-sm ${statusConfig.color}`}>
                              <StatusIcon className="w-4 h-4" />
                              {t(statusConfig.labelKey)}
                            </span>
                          </div>
                          <h3 className="font-semibold mb-1">{incident.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {incident.description}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{t("incidents.detected")}: {new Date(incident.detected_at).toLocaleDateString()}</span>
                            {incident.detection_source && (
                              <span className="capitalize">
                                {t("incidents.source_label")}: {incident.detection_source.replace("_", " ")}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="mt-3">
                        <Progress value={getStatusProgress(incident.status)} className="h-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredIncidents.length === 0 && (
            <EmptyState
              icon={Shield}
              title={t("incidents.no_incidents_title")}
              description={t("incidents.no_incidents_desc")}
              action={
                <Button onClick={() => setShowAddIncident(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("incidents.report_one")}
                </Button>
              }
            />
          )}
        </div>
      </ScrollArea>

      {/* Add Incident Sheet */}
      <Sheet open={showAddIncident} onOpenChange={setShowAddIncident}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("incidents.new_sheet_title")}</SheetTitle>
            <SheetDescription>{t("incidents.new_sheet_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="inc-title">{t("incidents.title_label")} *</Label>
              <Input
                id="inc-title"
                value={newIncident.title}
                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                placeholder={t("incidents.title_label")}
                data-testid="incident-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inc-desc">{t("incidents.desc_label")} *</Label>
              <Textarea
                id="inc-desc"
                value={newIncident.description}
                onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                placeholder={t("description")}
                className="min-h-[100px]"
                data-testid="incident-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("incidents.category_label")}</Label>
                <Select
                  value={newIncident.category}
                  onValueChange={(v) => setNewIncident({ ...newIncident, category: v })}
                >
                  <SelectTrigger data-testid="incident-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("incidents.severity")}</Label>
                <Select
                  value={newIncident.severity}
                  onValueChange={(v) => setNewIncident({ ...newIncident, severity: v })}
                >
                  <SelectTrigger data-testid="incident-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {severities.map(sev => (
                      <SelectItem key={sev.value} value={sev.value}>{t(sev.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("incidents.detection_source_label")}</Label>
              <Select
                value={newIncident.detection_source}
                onValueChange={(v) => setNewIncident({ ...newIncident, detection_source: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {detectionSources.map(src => (
                    <SelectItem key={src.value} value={src.value}>{t(src.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAddIncident(false)}>{t("cancel")}</Button>
            <Button onClick={handleAddIncident} data-testid="save-incident-btn">{t("incidents.create_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Incident Detail Dialog */}
      <Dialog open={!!viewingIncident} onOpenChange={() => setViewingIncident(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewingIncident && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className={getSeverityBadge(viewingIncident.severity)}>
                    {viewingIncident.severity}
                  </Badge>
                  <Badge variant="secondary" className="capitalize">
                    {t(categories.find(c => c.value === viewingIncident.category)?.labelKey || "inc.cat.other")}
                  </Badge>
                </div>
                <DialogTitle className="text-xl">{viewingIncident.title}</DialogTitle>
                <DialogDescription>
                  {t("incidents.detail_detected")}: {new Date(viewingIncident.detected_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status Progress */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">Incident Lifecycle</h4>
                  <div className="flex items-center gap-1">
                    {statuses.map((status, idx) => {
                      const currentIdx = statuses.findIndex(s => s.value === viewingIncident.status);
                      const isComplete = idx <= currentIdx;
                      const isCurrent = idx === currentIdx;
                      const StatusIcon = status.icon;

                      return (
                        <div key={status.value} className="flex items-center flex-1">
                          <button
                            onClick={() => handleUpdateStatus(viewingIncident.incident_id, status.value)}
                            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors w-full
                              ${isCurrent ? 'bg-primary/10' : isComplete ? 'bg-muted' : 'hover:bg-muted/50'}`}
                            data-testid={`status-btn-${status.value}`}
                          >
                            <StatusIcon className={`w-5 h-5 ${isComplete ? status.color : 'text-muted-foreground'}`} />
                            <span className={`text-xs ${isComplete ? 'font-medium' : 'text-muted-foreground'}`}>
                              {t(status.labelKey)}
                            </span>
                          </button>
                          {idx < statuses.length - 1 && (
                            <ArrowRight className="w-4 h-4 text-muted-foreground mx-1 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">{viewingIncident.description}</p>
                </div>

                {/* Timeline */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">Timeline</h4>
                  <div className="space-y-3">
                    {timeline.map((entry, idx) => (
                      <div key={entry.timeline_id} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                        <div className="flex-1">
                          <p className="text-sm font-medium capitalize">
                            {entry.action.replace("_", " ")}
                          </p>
                          <p className="text-xs text-muted-foreground">{entry.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(entry.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {timeline.length === 0 && (
                      <p className="text-sm text-muted-foreground">No timeline entries yet</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Incidents;
